'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useSeafarerProfile } from '@/contexts/SeafarerProfileContext';
import { useCertificates } from '@/contexts/CertificatesContext';
import { needsExpiryAttention } from '@/lib/certificate-alerts';
import { Bell, LogOut, User, Menu, X, Radio, LifeBuoy, Inbox, FolderLock } from 'lucide-react';

const AlertDrawer = dynamic(() => import('@/components/AlertDrawer'), { ssr: false });

function getInitials(name: string) {
  return name
    .split(' ')
    .map((part) => part.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function AccountAvatar({ name, photoUrl, size = 'md' }: { name: string; photoUrl?: string; size?: 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'w-9 h-9' : 'w-8 h-8';
  const initials = getInitials(name);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className={`${sizeClass} rounded-full object-cover border border-border`}
      />
    );
  }

  return (
    <div className={`${sizeClass} rounded-full bg-secondary flex items-center justify-center border border-border`}>
      {initials ? (
        <span className="text-xs font-800 text-primary">{initials}</span>
      ) : (
        <User size={size === 'lg' ? 18 : 16} className="text-primary" />
      )}
    </div>
  );
}

export default function Topbar() {
  const router = useRouter();
  const pathname = usePathname();
  const { profile: authProfile, signOut, isAdmin } = useAuth();
  const { data: seafarerData } = useSeafarerProfile();
  const { certificates } = useCertificates();
  const seafarerProfile = seafarerData?.profile;
  const [menuOpen, setMenuOpen] = useState(false);
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);

  const displayName = authProfile?.full_name || seafarerProfile?.fullName || 'Seafarer';
  const displayRank = authProfile?.rank || seafarerProfile?.currentRank || '';
  const profilePhoto = seafarerProfile?.profilePhoto;

  const urgentCount = certificates.filter(needsExpiryAttention).length;

  const navClass = (href: string) => {
    const active = href === '/' ? pathname === '/' : pathname.startsWith(href);
    return `px-4 py-2 rounded-lg text-sm font-500 transition-colors ${
      active ? 'text-primary bg-secondary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
    }`;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
    } catch {
      toast.error('Failed to sign out. Please try again.');
    }
  };

  return (
    <>
      <header className="app-topbar sticky top-0 z-40 bg-card border-b border-border" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="app-topbar-inner max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-16 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 shrink-0">
            <AppLogo size={32} />
            <span className="app-topbar-brand font-bold text-lg text-primary tracking-tight">Seamigo</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1">
            <Link href="/" className={navClass('/')}>Home</Link>
            <Link href="/certificates" className={navClass('/certificates')}>Certificates</Link>
            <Link href="/vault" className={navClass('/vault')} title="Document Vault">
              <span className="flex items-center gap-1.5"><FolderLock size={13} />Vault</span>
            </Link>
            <Link href="/cv-builder" className={navClass('/cv-builder')}>CV</Link>
            <Link href="/profile" className={navClass('/profile')}>Profile</Link>
            <Link href="/support" className={navClass('/support')} title="Support">
              <span className="flex items-center gap-1.5"><LifeBuoy size={13} />Support</span>
            </Link>
            {isAdmin && (
              <>
                <Link href="/support-admin" className={navClass('/support-admin')} title="Admin: Support Inbox">
                  <span className="flex items-center gap-1.5"><Inbox size={13} />Inbox</span>
                </Link>
                <Link href="/developer-broadcast" className={navClass('/developer-broadcast')} title="Admin: Send Broadcast">
                  <span className="flex items-center gap-1.5"><Radio size={13} />Broadcast</span>
                </Link>
              </>
            )}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAlertDrawerOpen(true)}
              className="app-icon-button relative p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label={`Certificate alerts${urgentCount > 0 ? ` — ${urgentCount} need attention` : ''}`}
            >
              <Bell size={20} />
              {urgentCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-700 text-white flex items-center justify-center px-1 leading-none"
                  style={{ backgroundColor: 'var(--status-critical)' }}
                >
                  {urgentCount > 99 ? '99+' : urgentCount}
                </span>
              )}
            </button>

            <div className="hidden xl:flex items-center gap-2 pl-2 border-l border-border ml-1">
              <Link
                href="/profile"
                className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted transition-colors"
                aria-label="Open profile"
                title="Open profile"
              >
                <AccountAvatar name={displayName} photoUrl={profilePhoto} />
                <div className="hidden lg:block text-left">
                  <p className="text-sm font-600 text-foreground leading-tight">{displayName}</p>
                  {displayRank && <p className="text-xs text-muted-foreground leading-tight">{displayRank}</p>}
                </div>
              </Link>
              <button
                onClick={handleSignOut}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground ml-1"
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>

            {/* Compact browser/tablet menu; the native-width app uses bottom tabs. */}
            <button
              className="app-icon-button xl:hidden p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Compact browser/tablet menu */}
        {menuOpen && (
          <div className="xl:hidden border-t border-border bg-card px-4 py-3 flex flex-col gap-1 fade-in">
            <Link
              href="/profile"
              className="flex items-center gap-3 px-3 py-2 mb-2 rounded-lg hover:bg-muted transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              <AccountAvatar name={displayName} photoUrl={profilePhoto} size="lg" />
              <div>
                <p className="text-sm font-600 text-foreground">{displayName}</p>
                {displayRank && <p className="text-xs text-muted-foreground">{displayRank}</p>}
              </div>
            </Link>
            <Link href="/" className={`px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname === '/' ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
              Home
            </Link>
            <Link href="/certificates" className={`px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/certificates') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
              Certificates
            </Link>
            <Link href="/profile" className={`px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/profile') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
              My Profile
            </Link>
            <Link href="/cv-builder" className={`px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/cv-builder') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
              CV Builder
            </Link>
            <Link href="/vault" className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/vault') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
              <FolderLock size={16} />
              Vault
            </Link>
            <Link href="/notification-settings" className={`px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/notification-settings') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
              Notification Settings
            </Link>
            <Link href="/support" className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/support') && !pathname.startsWith('/support-admin') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
              <LifeBuoy size={16} />
              Support
            </Link>
            {isAdmin && (
              <>
                <Link href="/support-admin" className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/support-admin') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
                  <Inbox size={16} />
                  Support Inbox
                </Link>
                <Link href="/developer-broadcast" className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-500 transition-colors ${pathname.startsWith('/developer-broadcast') ? 'text-primary bg-secondary' : 'text-foreground hover:bg-muted'}`} onClick={() => setMenuOpen(false)}>
                  <Radio size={16} />
                  Broadcast
                </Link>
              </>
            )}
            <button
              onClick={() => { setMenuOpen(false); handleSignOut(); }}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-500 text-muted-foreground hover:bg-muted transition-colors mt-1"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        )}
      </header>

      {alertDrawerOpen && <AlertDrawer open={alertDrawerOpen} onClose={() => setAlertDrawerOpen(false)} />}
    </>
  );
}
